import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import PageHeader from '../shell/PageHeader';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { Field, Modal } from '../patterns';
import {
  getBackCaptureCombo,
  startBackCapture,
  completeBackCapture,
  endEarlyBackCapture,
  getBackCaptureMixPreview,
  BackCaptureCombo,
  BackCaptureMixPreview,
  WeatherSnapshot,
} from '../../services/backCaptureService';
import { getTenantInfo } from '../../services/tenantService';
import {
  sprayService,
  Orchard,
  SprayMachine,
  SprayOperator,
} from '../../services/sprayService';
import {
  bucketTemperature,
  bucketWind,
  bucketPrecipitation,
  findClosestWithin48h,
  WeatherEntry,
  WeatherThresholds,
} from './weatherBucketing';
import './BackCaptureFormPage.css';

const DEFAULT_WEATHER: WeatherSnapshot = {
  temperatureCategory: 'optimal',
  windCategory: 'calm',
  precipitationCategory: 'none',
};

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
const TWENTY_FOUR_H_MS = 24 * 60 * 60 * 1000;

const TEMP_OPTIONS: Array<{ value: WeatherSnapshot['temperatureCategory']; labelKey: string }> = [
  { value: 'cold',    labelKey: 'spray.backCapture.form.weather.temp.cold' },
  { value: 'optimal', labelKey: 'spray.backCapture.form.weather.temp.optimal' },
  { value: 'hot',     labelKey: 'spray.backCapture.form.weather.temp.hot' },
];
const WIND_OPTIONS: Array<{ value: WeatherSnapshot['windCategory']; labelKey: string }> = [
  { value: 'calm',   labelKey: 'spray.backCapture.form.weather.wind.calm' },
  { value: 'breeze', labelKey: 'spray.backCapture.form.weather.wind.breeze' },
  { value: 'strong', labelKey: 'spray.backCapture.form.weather.wind.strong' },
];
const PRECIP_OPTIONS: Array<{ value: WeatherSnapshot['precipitationCategory']; labelKey: string }> = [
  { value: 'none',  labelKey: 'spray.backCapture.form.weather.precip.none' },
  { value: 'light', labelKey: 'spray.backCapture.form.weather.precip.light' },
  { value: 'heavy', labelKey: 'spray.backCapture.form.weather.precip.heavy' },
];

const BackCaptureFormPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { comboId } = useParams<{ comboId: string }>();

  const [combo, setCombo] = useState<BackCaptureCombo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrideOn, setOverrideOn] = useState(false);
  const [thresholds, setThresholds] = useState<WeatherThresholds | null>(null);
  const [overrides, setOverrides] = useState<Set<string>>(new Set());
  const [noStartReading, setNoStartReading] = useState(false);
  const [noEndReading, setNoEndReading] = useState(false);

  // Header-card reference data
  const [orchardMap, setOrchardMap] = useState<Map<string, string>>(new Map());
  const [machineMap, setMachineMap] = useState<Map<string, string>>(new Map());
  const [operatorMap, setOperatorMap] = useState<Map<string, string>>(new Map());

  // Mix preview for products-consumed panel
  const [mixPreview, setMixPreview] = useState<BackCaptureMixPreview | null>(null);

  // Ref to track latest overrides without closure issues
  const overridesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    overridesRef.current = overrides;
  }, [overrides]);

  // Form state
  const [startedAt, setStartedAt] = useState('');
  const [weatherStart, setWeatherStart] = useState<WeatherSnapshot>(DEFAULT_WEATHER);
  const [endTime, setEndTime] = useState('');
  const [weatherEnd, setWeatherEnd] = useState<WeatherSnapshot>(DEFAULT_WEATHER);
  const [tankCount, setTankCount] = useState<number | ''>('');
  const [ppeChecks, setPpeChecks] = useState<Array<{ item: string; checked: boolean }>>([]);

  // Modal + submit state
  const [showEndEarly, setShowEndEarly] = useState(false);
  const [endEarlyReason, setEndEarlyReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.backCapture.list.title'), to: '/protection/back-capture' },
    { label: t('spray.backCapture.form.title') },
  ]);

  // Load combo + tenant settings
  useEffect(() => {
    if (!comboId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getBackCaptureCombo(comboId)
      .then((c) => {
        if (cancelled) return;
        setCombo(c);
        setPpeChecks((c.ppeChecklist ?? []).map(r => ({ item: r.item, checked: !!r.checked })));
        // Pre-fill startedAt for in_progress combos (already saved to server)
        if (c.startedAt) {
          setStartedAt(new Date(c.startedAt).toISOString().slice(0, 16));
        }
      })
      .catch(() => { if (!cancelled) setError(t('spray.backCapture.form.loadError')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    getTenantInfo()
      .then((tenant) => {
        if (cancelled) return;
        setOverrideOn(!!(tenant?.applicationSettings?.allowBackCaptureBeyond90Days ?? tenant?.spraySettings?.allowBackCaptureBeyond90Days));
        if (tenant?.weatherThresholds) {
          setThresholds(tenant.weatherThresholds);
        }
      })
      .catch(() => { /* silent — assume override off */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comboId]);

  // Fetch reference data for header card
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      sprayService.getOrchards().catch(() => [] as Orchard[]),
      sprayService.getMachines().catch(() => [] as SprayMachine[]),
      sprayService.getOperators().catch(() => [] as SprayOperator[]),
    ]).then(([orchards, machines, operators]) => {
      if (cancelled) return;
      setOrchardMap(new Map(orchards.map(o => [o._id, o.orchardName])));
      setMachineMap(new Map(machines.map(m => [m._id, m.name])));
      setOperatorMap(new Map(operators.map(op => [op._id, op.name])));
    });
    return () => { cancelled = true; };
  }, []);

  // One-shot fetch of mix preview
  useEffect(() => {
    if (!comboId) return;
    let cancelled = false;
    getBackCaptureMixPreview(comboId)
      .then(p => { if (!cancelled) setMixPreview(p); })
      .catch(() => { /* silent — preview is optional */ });
    return () => { cancelled = true; };
  }, [comboId]);

  // Debounced weather auto-fill for startedAt
  useEffect(() => {
    if (!startedAt || !thresholds) return;
    let live = true;
    const id = setTimeout(async () => {
      try {
        const res = await axios.get<{ entries: WeatherEntry[]; hours: number }>(
          '/api/v1/tenants/me/weather/recent',
          { params: { hours: 48 } }
        );
        if (!live) return;
        const entries = res.data?.entries ?? [];
        const target = new Date(startedAt).toISOString();
        const closest = findClosestWithin48h(entries, target);
        if (!closest) {
          setNoStartReading(true);
          return;
        }
        setNoStartReading(false);
        setWeatherStart(prev => ({
          ...prev,
          temperatureCategory:   overridesRef.current.has('startTemp')   ? prev.temperatureCategory   : (closest.temp          != null ? bucketTemperature(closest.temp, thresholds)            : prev.temperatureCategory),
          windCategory:          overridesRef.current.has('startWind')   ? prev.windCategory          : (closest.windSpeed     != null ? bucketWind(closest.windSpeed, thresholds)              : prev.windCategory),
          precipitationCategory: overridesRef.current.has('startPrecip') ? prev.precipitationCategory : (closest.precipitation != null ? bucketPrecipitation(closest.precipitation, thresholds) : prev.precipitationCategory),
        }));
      } catch {
        // Silent — admin can still enter manually.
      }
    }, 400);
    return () => { live = false; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startedAt, thresholds]);

  // Debounced weather auto-fill for endTime
  useEffect(() => {
    if (!endTime || !thresholds) return;
    let live = true;
    const id = setTimeout(async () => {
      try {
        const res = await axios.get<{ entries: WeatherEntry[]; hours: number }>(
          '/api/v1/tenants/me/weather/recent',
          { params: { hours: 48 } }
        );
        if (!live) return;
        const entries = res.data?.entries ?? [];
        const target = new Date(endTime).toISOString();
        const closest = findClosestWithin48h(entries, target);
        if (!closest) {
          setNoEndReading(true);
          return;
        }
        setNoEndReading(false);
        setWeatherEnd(prev => ({
          ...prev,
          temperatureCategory:   overridesRef.current.has('endTemp')   ? prev.temperatureCategory   : (closest.temp          != null ? bucketTemperature(closest.temp, thresholds)            : prev.temperatureCategory),
          windCategory:          overridesRef.current.has('endWind')   ? prev.windCategory          : (closest.windSpeed     != null ? bucketWind(closest.windSpeed, thresholds)              : prev.windCategory),
          precipitationCategory: overridesRef.current.has('endPrecip') ? prev.precipitationCategory : (closest.precipitation != null ? bucketPrecipitation(closest.precipitation, thresholds) : prev.precipitationCategory),
        }));
      } catch {
        // Silent — admin can still enter manually.
      }
    }, 400);
    return () => { live = false; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTime, thresholds]);

  if (loading) {
    return (
      <div className="back-capture-form">
        <PageHeader title={t('spray.backCapture.form.title')} />
        <div className="back-capture-form__status" role="status">
          {t('spray.backCapture.form.loading')}
        </div>
      </div>
    );
  }

  if (error || !combo) {
    return (
      <div className="back-capture-form">
        <PageHeader title={t('spray.backCapture.form.title')} />
        <div className="back-capture-form__error" role="alert">
          {error ?? t('spray.backCapture.form.loadError')}
        </div>
      </div>
    );
  }

  const status = combo.status;
  const isReadOnly = status === 'completed' || status === 'incomplete';

  if (isReadOnly) {
    return (
      <div className="back-capture-form">
        <PageHeader
          title={t('spray.backCapture.form.title')}
          meta={t('spray.backCapture.form.subtitle')}
        />
        <div
          className="back-capture-form__readonly"
          role="status"
          data-testid="back-capture-form-readonly"
        >
          <p>{t('spray.backCapture.form.readOnly.note')}</p>
          <Link to={`/protection/sessions/${combo.sessionId}`}>
            {t('spray.backCapture.form.action.viewSession')}
          </Link>
        </div>
      </div>
    );
  }

  const hasStart = status === 'in_progress';

  // ── Header card helpers ───────────────────────────────────────────────────
  const parent = combo.parent;
  const sessionLabel = parent.seq
    ? (parent.name ? `SES-${parent.seq} · ${parent.name}` : `SES-${parent.seq}`)
    : '—';
  const orchardNames = (parent.orchardIds ?? [])
    .map(id => orchardMap.get(id) ?? id)
    .join(', ') || '—';
  const machineName = machineMap.get(combo.machineId) ?? combo.machineId;
  const operatorName = operatorMap.get(combo.operatorId) ?? combo.operatorId;

  // ── Validation ────────────────────────────────────────────────────────
  const startedAtMs = startedAt ? new Date(startedAt).getTime() : NaN;
  const endTimeMs   = endTime   ? new Date(endTime).getTime()   : NaN;
  const now = Date.now();
  const endBeforeStart =
    !Number.isNaN(startedAtMs) && !Number.isNaN(endTimeMs) && endTimeMs <= startedAtMs;
  const startTooOld =
    !overrideOn && !Number.isNaN(startedAtMs) && startedAtMs < now - NINETY_DAYS_MS;
  const startTooFuture =
    !Number.isNaN(startedAtMs) && startedAtMs > now + TWENTY_FOUR_H_MS;

  const canSaveStart = !hasStart && !!startedAt && !startTooOld && !startTooFuture && !saving;
  const canSaveComplete = hasStart
    ? (!!endTime && tankCount !== '' && !endBeforeStart && !saving)
    : (!!startedAt && !!endTime && tankCount !== '' && !endBeforeStart && !startTooOld && !startTooFuture && !saving);
  const canConfirmEndEarly =
    hasStart && !!endTime && tankCount !== '' && !!endEarlyReason.trim() && !endBeforeStart && !saving;

  // ── Submit handlers ───────────────────────────────────────────────────
  const handleSaveStart = async () => {
    if (!comboId || !canSaveStart) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await startBackCapture(comboId, {
        startedAt: new Date(startedAt).toISOString(),
        weatherStart,
        ppeChecks,
      });
      navigate('/protection/back-capture');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setSubmitError(axiosErr?.response?.data?.error ?? t('spray.backCapture.form.error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveComplete = async () => {
    if (!comboId || !canSaveComplete) return;
    setSaving(true);
    setSubmitError(null);
    try {
      if (!hasStart) {
        const startRes = await startBackCapture(comboId, {
          startedAt: new Date(startedAt).toISOString(),
          weatherStart,
          ppeChecks,
        });
        // /start returns { combo, parent } separately — re-attach so the
        // pre-navigate render doesn't crash on parent.seq.
        setCombo(prev => ({
          ...startRes.combo,
          parent: startRes.parent ?? prev?.parent,
        }));
      }
      await completeBackCapture(comboId, {
        endTime: new Date(endTime).toISOString(),
        weatherEnd,
        tankCount: Number(tankCount),
      });
      navigate('/protection/back-capture');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setSubmitError(axiosErr?.response?.data?.error ?? t('spray.backCapture.form.error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmEndEarly = async () => {
    if (!comboId || !canConfirmEndEarly) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await endEarlyBackCapture(comboId, {
        endTime: new Date(endTime).toISOString(),
        weatherEnd,
        tankCount: Number(tankCount),
        reason: endEarlyReason.trim(),
      });
      navigate('/protection/back-capture');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setSubmitError(axiosErr?.response?.data?.error ?? t('spray.backCapture.form.error.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="back-capture-form">
      <PageHeader
        title={t('spray.backCapture.form.title')}
        meta={t('spray.backCapture.form.subtitle')}
      />

      {/* ── Header card ─────────────────────────────────────────────────── */}
      <dl className="back-capture-form__header-card" data-testid="back-capture-header-card">
        <div>
          <dt>{t('spray.backCapture.form.header.session')}</dt>
          <dd>{sessionLabel}</dd>
        </div>
        <div>
          <dt>{t('spray.backCapture.form.header.orchards')}</dt>
          <dd>{orchardNames}</dd>
        </div>
        <div>
          <dt>{t('spray.backCapture.form.header.machine')}</dt>
          <dd>{machineName}</dd>
        </div>
        <div>
          <dt>{t('spray.backCapture.form.header.operator')}</dt>
          <dd>{operatorName}</dd>
        </div>
      </dl>

      {/* ── Variant C grid: Start | chevron | End ───────────────────────── */}
      <div className="back-capture-form__grid">

        {/* Start card */}
        <div className="back-capture-form__card" data-testid="back-capture-start">
          <h4 className="back-capture-form__section-title">
            {t('spray.backCapture.form.section.start')}
          </h4>

          <Field label={t('spray.backCapture.form.field.startTime')} controlId="bc-start-time"
            errorText={
              startTooOld
                ? t('spray.backCapture.form.error.startTooOld')
                : startTooFuture
                  ? t('spray.backCapture.form.error.startTooFuture')
                  : undefined
            }
          >
            <input
              id="bc-start-time"
              type="datetime-local"
              value={startedAt}
              disabled={hasStart}
              onChange={e => setStartedAt(e.target.value)}
            />
          </Field>

          {overrideOn && (
            <p className="back-capture-form__hint">
              {t('spray.backCapture.form.weather.bypassHint')}
            </p>
          )}

          <div className="back-capture-form__weather-grid">
            <Field label={t('spray.backCapture.form.field.tempStart')} controlId="bc-temp-start">
              <select
                id="bc-temp-start"
                value={weatherStart.temperatureCategory}
                disabled={hasStart}
                onChange={e => {
                  setOverrides(prev => { const next = new Set(prev); next.add('startTemp'); return next; });
                  setWeatherStart({ ...weatherStart, temperatureCategory: e.target.value as WeatherSnapshot['temperatureCategory'] });
                }}
              >
                {TEMP_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </Field>

            <Field label={t('spray.backCapture.form.field.windStart')} controlId="bc-wind-start">
              <select
                id="bc-wind-start"
                value={weatherStart.windCategory}
                disabled={hasStart}
                onChange={e => {
                  setOverrides(prev => { const next = new Set(prev); next.add('startWind'); return next; });
                  setWeatherStart({ ...weatherStart, windCategory: e.target.value as WeatherSnapshot['windCategory'] });
                }}
              >
                {WIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </Field>

            <Field label={t('spray.backCapture.form.field.precipStart')} controlId="bc-precip-start">
              <select
                id="bc-precip-start"
                value={weatherStart.precipitationCategory}
                disabled={hasStart}
                onChange={e => {
                  setOverrides(prev => { const next = new Set(prev); next.add('startPrecip'); return next; });
                  setWeatherStart({ ...weatherStart, precipitationCategory: e.target.value as WeatherSnapshot['precipitationCategory'] });
                }}
              >
                {PRECIP_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </Field>
          </div>

          {noStartReading && (
            <p className="back-capture-form__hint" data-testid="no-start-weather">
              {t('spray.backCapture.form.weather.noReading')}
            </p>
          )}

          {ppeChecks.length > 0 && (
            <fieldset className="back-capture-form__ppe-list">
              <legend>{t('spray.backCapture.form.field.ppeChecklist')}</legend>
              {ppeChecks.map((row, idx) => (
                <label key={row.item}>
                  <input
                    type="checkbox"
                    checked={row.checked}
                    disabled={hasStart}
                    onChange={e => {
                      const next = [...ppeChecks];
                      next[idx] = { ...row, checked: e.target.checked };
                      setPpeChecks(next);
                    }}
                  />
                  {row.item}
                </label>
              ))}
            </fieldset>
          )}
        </div>

        {/* Chevron divider */}
        <div className="back-capture-form__chevron" aria-hidden="true">›</div>

        {/* End card */}
        <div className="back-capture-form__card" data-testid="back-capture-end">
          <h4 className="back-capture-form__section-title">
            {t('spray.backCapture.form.section.end')}
          </h4>

          <Field label={t('spray.backCapture.form.field.endTime')} controlId="bc-end-time"
            errorText={endBeforeStart ? t('spray.backCapture.form.error.endBeforeStart') : undefined}
          >
            <input
              id="bc-end-time"
              type="datetime-local"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
            />
          </Field>

          <div className="back-capture-form__weather-grid">
            <Field label={t('spray.backCapture.form.field.tempEnd')} controlId="bc-temp-end">
              <select
                id="bc-temp-end"
                value={weatherEnd.temperatureCategory}
                onChange={e => {
                  setOverrides(prev => { const next = new Set(prev); next.add('endTemp'); return next; });
                  setWeatherEnd({ ...weatherEnd, temperatureCategory: e.target.value as WeatherSnapshot['temperatureCategory'] });
                }}
              >
                {TEMP_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </Field>

            <Field label={t('spray.backCapture.form.field.windEnd')} controlId="bc-wind-end">
              <select
                id="bc-wind-end"
                value={weatherEnd.windCategory}
                onChange={e => {
                  setOverrides(prev => { const next = new Set(prev); next.add('endWind'); return next; });
                  setWeatherEnd({ ...weatherEnd, windCategory: e.target.value as WeatherSnapshot['windCategory'] });
                }}
              >
                {WIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </Field>

            <Field label={t('spray.backCapture.form.field.precipEnd')} controlId="bc-precip-end">
              <select
                id="bc-precip-end"
                value={weatherEnd.precipitationCategory}
                onChange={e => {
                  setOverrides(prev => { const next = new Set(prev); next.add('endPrecip'); return next; });
                  setWeatherEnd({ ...weatherEnd, precipitationCategory: e.target.value as WeatherSnapshot['precipitationCategory'] });
                }}
              >
                {PRECIP_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
              </select>
            </Field>
          </div>

          {noEndReading && (
            <p className="back-capture-form__hint" data-testid="no-end-weather">
              {t('spray.backCapture.form.weather.noReading')}
            </p>
          )}

          <Field label={t('spray.backCapture.form.field.tankCount')} controlId="bc-tank-count">
            <input
              id="bc-tank-count"
              type="number"
              min={0.1}
              step="0.1"
              value={tankCount}
              onChange={e => setTankCount(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </Field>

          {/* Products consumed preview */}
          {tankCount !== '' && Number(tankCount) > 0 && mixPreview && mixPreview.perTankMix.length > 0 && (
            <div className="back-capture-form__products" data-testid="products-consumed-preview">
              <div className="back-capture-form__products-title">
                {t('spray.backCapture.form.field.productsConsumed')}
              </div>
              <ul className="back-capture-form__products-list">
                {mixPreview.perTankMix.map(p => {
                  const quantity = Math.round(p.dosePerTank * Number(tankCount) * 100) / 100;
                  return (
                    <li key={p.productId}>
                      <span className="back-capture-form__products-name">{p.productName}</span>
                      <span className="back-capture-form__products-qty">
                        {quantity} {p.doseUnit}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="back-capture-form__actions" data-testid="back-capture-actions">
        {!hasStart && (
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            disabled={!canSaveStart}
            onClick={handleSaveStart}
          >
            {t('spray.backCapture.form.action.saveStart')}
          </button>
        )}
        <button
          type="button"
          className="pat-btn pat-btn--primary"
          disabled={!canSaveComplete}
          onClick={handleSaveComplete}
        >
          {t('spray.backCapture.form.action.saveComplete')}
        </button>
        {hasStart && (
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={() => setShowEndEarly(true)}
            disabled={saving}
          >
            {t('spray.backCapture.form.action.endEarly')}
          </button>
        )}
        <button
          type="button"
          className="pat-btn pat-btn--outline"
          disabled={hasStart ? saving : (!canSaveStart || saving)}
          onClick={hasStart
            ? () => navigate('/protection/back-capture')
            : handleSaveStart}
        >
          {t('spray.backCapture.form.action.saveLater')}
        </button>
      </div>

      <Modal
        show={showEndEarly}
        onHide={() => setShowEndEarly(false)}
        title={t('spray.backCapture.form.action.endEarly')}
        size="md"
      >
        <Modal.Body>
          <div data-testid="end-early-modal">
            <Field
              label={t('spray.backCapture.form.field.endEarlyReason')}
              controlId="bc-end-early-reason"
            >
              <textarea
                id="bc-end-early-reason"
                value={endEarlyReason}
                onChange={e => setEndEarlyReason(e.target.value)}
              />
            </Field>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--outline"
            onClick={() => setShowEndEarly(false)}
            disabled={saving}
          >
            {t('spray.backCapture.form.action.cancel')}
          </button>
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            disabled={!canConfirmEndEarly}
            onClick={handleConfirmEndEarly}
          >
            {t('spray.backCapture.form.action.confirmEndEarly')}
          </button>
        </Modal.Footer>
      </Modal>

      {submitError && (
        <div role="alert" className="back-capture-form__error">{submitError}</div>
      )}
    </div>
  );
};

export default BackCaptureFormPage;
